/**
 * Customer card — grid-view variant of `CustomerRow`.
 *
 * Same data, tile shape. Used when the list-page toolbar's view-toggle is
 * set to "grid". The shape is intentionally bigger than the row's first
 * column block: in grid mode the Customer's identity is the only thing
 * that has to fit, so we let typography breathe.
 *
 * @see prototype/sales/customers.html — grid-view button (`.view-toggle [aria-label="Grid view"]`)
 */
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { StatusPill } from "@flint/ui";
import { paiseToInrShort } from "../../../lib/format.ts";
import { CustomerAvatar } from "./customer-avatar.tsx";

type ListRow = z.infer<typeof contract.listRow>;

export interface CustomerCardProps {
  customer: ListRow;
}

export function CustomerCard({ customer }: CustomerCardProps) {
  const isArchived = customer.archivedAt !== null;
  const hasInvoices = customer.invoiceCount > 0;
  const hasOverdue = customer.overdueInvoiceCount > 0;
  const hasOutstanding = BigInt(customer.outstandingPaise) > 0n;

  return (
    <Link
      to="/sales/customers/$customerId"
      params={{ customerId: customer.id }}
      className="group flex flex-col gap-3 p-4 bg-paper border border-line rounded-md hover:border-line-2 hover:bg-raised transition-colors"
    >
      {/* Header row: avatar + chevron */}
      <div className="flex items-start justify-between">
        <CustomerAvatar name={customer.name} size="lg" />
        <ChevronRight
          size={14}
          strokeWidth={2}
          aria-hidden
          className="text-muted group-hover:text-ink-2 transition-colors mt-1"
        />
      </div>

      {/* Identity */}
      <div className="min-w-0">
        <p className="font-medium text-ink truncate">{customer.name}</p>
        <p className="text-xs text-muted mt-0.5 truncate">
          {customer.gstin ? (
            <span className="font-mono">{customer.gstin}</span>
          ) : (
            <span className="italic">No GSTIN</span>
          )}
        </p>
        {customer.city || customer.contactName ? (
          <p className="text-xs text-muted truncate">
            {[customer.city, customer.contactName].filter(Boolean).join(" · ")}
          </p>
        ) : null}
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        {isArchived ? (
          <StatusPill tone="muted">Archived</StatusPill>
        ) : hasOverdue ? (
          <StatusPill tone="bad">{customer.overdueInvoiceCount} overdue</StatusPill>
        ) : hasOutstanding ? (
          <StatusPill tone="warn">Outstanding</StatusPill>
        ) : hasInvoices ? (
          <StatusPill tone="ok">Clear</StatusPill>
        ) : (
          <StatusPill tone="muted">No data yet</StatusPill>
        )}
        {customer.paymentTerms ? <StatusPill tone="sky">{customer.paymentTerms}</StatusPill> : null}
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-line">
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wider text-muted">Outstanding</span>
          <span className="text-sm font-medium text-ink num">
            {paiseToInrShort(customer.outstandingPaise)}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wider text-muted">Billed FY</span>
          <span className="text-sm font-medium text-ink num">
            {paiseToInrShort(customer.ytdInvoicedPaise)}
          </span>
        </div>
      </div>
    </Link>
  );
}
